import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { companyInfo, logos } from '../../data/mock';

// Animation styles for 会社概要 highlight
const animationStyles = `
  @keyframes shimmer-slow {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @keyframes pulse-ring {
    0% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.4); }
    70% { box-shadow: 0 0 0 6px rgba(217, 119, 6, 0); }
    100% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
  }
  .animate-shimmer-slow {
    animation: shimmer-slow 3s ease-in-out infinite;
  }
  .animate-pulse-ring {
    animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
`;

const Header = ({ onCtaClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isCompanyPage = location.pathname === '/company';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'パートナーシップ', href: '#partnership', sectionId: 'partnership' },
    { label: '実績', href: '#results', sectionId: 'results' },
    { label: 'ブランディング', href: '#branding', sectionId: 'branding' },
    { label: '成果保証', href: '#guarantee', sectionId: 'guarantee' },
    { label: 'プロセス', href: '#process', sectionId: 'process' },
    { label: '会社概要', href: '/company', external: true, highlight: true },
  ];

  const handleNavClick = (e, link) => {
    if (link.external) return; // Let Link component handle external routes
    
    e.preventDefault();
    
    if (isCompanyPage) {
      // Navigate to home page first, then scroll to section
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(link.sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Already on landing page, just scroll
      const element = document.getElementById(link.sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <style>{animationStyles}</style>
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled
            ? 'bg-stone-50 backdrop-blur-md shadow-sm'
            : 'bg-stone-50/80 backdrop-blur-sm'
        }`}
      >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Click to scroll to top */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img 
              src={logos.light} 
              alt={companyInfo.name}
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.external ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-sm transition-colors relative ${
                    link.highlight 
                      ? 'text-stone-800 font-medium group'
                      : 'text-stone-600 hover:text-stone-900 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-px after:bg-amber-700 hover:after:w-full after:transition-all'
                  }`}
                >
                  {link.highlight ? (
                    <span className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full overflow-hidden">
                      {/* Animated gradient background */}
                      <span className="absolute inset-0 bg-gradient-to-r from-amber-100 via-amber-200 to-amber-100 bg-[length:200%_100%] animate-shimmer-slow rounded-full" />
                      {/* Subtle pulse ring */}
                      <span className="absolute inset-0 rounded-full animate-pulse-ring" />
                      <span className="relative z-10">{link.label}</span>
                      <span className="relative z-10 w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                    </span>
                  ) : (
                    link.label
                  )}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  className="text-sm text-stone-600 hover:text-stone-900 transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-px after:bg-amber-700 hover:after:w-full after:transition-all cursor-pointer"
                >
                  {link.label}
                </a>
              )
            ))}
          </nav>

          {/* CTA Button */}
          {!isCompanyPage ? (
            <div className="hidden lg:block">
              <Button
                onClick={onCtaClick}
                className="bg-stone-800 hover:bg-stone-700 text-stone-50 px-6 py-2 text-sm font-normal tracking-wide transition-all hover:shadow-lg"
              >
                適合性を確認
              </Button>
            </div>
          ) : (
            <div className="hidden lg:block w-[120px]"></div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-stone-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-stone-50 border-t border-stone-200 py-4 animate-in slide-in-from-top-2">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                link.external ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-stone-600 hover:text-stone-900 px-4 py-2 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-stone-600 hover:text-stone-900 px-4 py-2 transition-colors cursor-pointer"
                    onClick={(e) => {
                      handleNavClick(e, link);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {link.label}
                  </a>
                )
              ))}
              {!isCompanyPage && (
                <div className="px-4 pt-2">
                  <Button
                    onClick={() => {
                      onCtaClick();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-stone-800 hover:bg-stone-700 text-stone-50"
                  >
                    適合性を確認
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;
